import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useQuizAnalytics, useQuizEmails } from '@/hooks/useQuizAnalytics';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import { Users, Mail, TrendingUp, Compass } from 'lucide-react';
import { format } from 'date-fns';

const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];

const STYLE_LABELS: Record<string, string> = {
  adventure: '🏔️ Adventure',
  relaxation: '🏖️ Relaxation',
  culture: '🏛️ Culture',
  foodie: '🍜 Food & Nightlife',
};

const BUDGET_LABELS: Record<string, string> = {
  budget: '🎒 Budget',
  mid: '🏨 Mid-Range',
  luxury: '✨ Luxury',
  mixed: '🔀 Mixed',
};

export default function QuizAnalytics() {
  const { data, isLoading } = useQuizAnalytics();
  const { data: emails } = useQuizEmails();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Quiz Analytics</h1>
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  const stats = data?.stats;
  const results = data?.results || [];

  const travelStyleData = Object.entries(stats?.travelStyleDistribution || {}).map(([name, value]) => ({
    name: STYLE_LABELS[name] || name,
    value,
  }));

  const budgetData = Object.entries(stats?.budgetDistribution || {}).map(([name, value]) => ({
    name: BUDGET_LABELS[name] || name,
    value,
  }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Quiz Analytics</h1>
        <p className="text-muted-foreground">Track quiz submissions and user preferences</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Submissions</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalSubmissions || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Email Captures</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{emails?.length || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats?.emailCaptureRate.toFixed(1)}% capture rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Top Style</CardTitle>
            <Compass className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {travelStyleData.length > 0 
                ? travelStyleData.sort((a, b) => b.value - a.value)[0]?.name 
                : 'N/A'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">This Week</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {results.filter(r => {
                if (!r.created_at) return false;
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                return new Date(r.created_at) > weekAgo;
              }).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Travel Style Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {travelStyleData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={travelStyleData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {travelStyleData.map((_, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-10">No data yet</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Budget Preferences</CardTitle>
          </CardHeader>
          <CardContent>
            {budgetData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={budgetData}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-muted-foreground py-10">No data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email Captures Table */}
      <Card>
        <CardHeader>
          <CardTitle>Email Captures</CardTitle>
        </CardHeader>
        <CardContent>
          {emails && emails.length > 0 ? (
            <div className="space-y-2">
              {emails.slice(0, 20).map((entry, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{entry.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                      {STYLE_LABELS[entry.travel_style] || entry.travel_style}
                    </Badge>
                    {entry.created_at && (
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(entry.created_at), 'MMM d, yyyy')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-6">No emails captured yet</p>
          )}
        </CardContent>
      </Card>

      {/* Recent Submissions */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Submissions</CardTitle>
        </CardHeader>
        <CardContent>
          {results.length > 0 ? (
            <div className="space-y-2">
              {results.slice(0, 10).map((result) => (
                <div key={result.id} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center gap-2">
                    <Badge>{STYLE_LABELS[result.travel_style] || result.travel_style}</Badge>
                    {result.budget_preference && (
                      <Badge variant="outline">{BUDGET_LABELS[result.budget_preference] || result.budget_preference}</Badge>
                    )}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {result.created_at && format(new Date(result.created_at), 'MMM d, h:mm a')}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-6">No submissions yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
